import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiMenu,
    FiX,
    FiSun,
    FiMoon,
    FiGlobe,
    FiHome,
    FiTool,
    FiBook,
    FiInfo,
    FiChevronRight,
    FiCpu,
    FiCode,
    FiHelpCircle,
    FiMail,
    FiShield,
    FiFileText,
} from "react-icons/fi";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import MewSeedsLogo from "../../assets/MewSeeds_logo_2.png";

interface MobileMenuItem {
    id: string;
    label: string;
    href?: string;
    icon: React.ComponentType<any>;
    children?: MobileMenuItem[];
}

const Header = () => {
    const { isDark, toggleTheme } = useTheme();
    const { language, setLanguage, t } = useLanguage();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
    const [expandedMobileItems, setExpandedMobileItems] = useState<string[]>(
        []
    );

    const languages = [
        { code: "ko", name: "한국어", flag: "🇰🇷" },
        { code: "en", name: "English", flag: "🇺🇸" },
        { code: "ja", name: "日本語", flag: "🇯🇵" },
        { code: "zh", name: "中文", flag: "🇨🇳" },
    ];

    const navItems = [
        { href: "/", label: t("nav.home"), icon: FiHome },
        { href: "/tools", label: t("nav.tools"), icon: FiTool },
        { href: "/guide", label: t("nav.guide"), icon: FiBook },
        { href: "/about", label: t("nav.about"), icon: FiInfo },
    ];

    // 모바일 메뉴용 전체 네비게이션 항목
    const mobileMenuItems: MobileMenuItem[] = [
        {
            id: "home",
            label: t("nav.home"),
            href: "/",
            icon: FiHome,
        },
        {
            id: "tools",
            label: t("nav.tools"),
            icon: FiTool,
            children: [
                {
                    id: "tools-overview",
                    label: t("sidebar.toolsOverview"),
                    href: "/tools",
                    icon: FiTool,
                },
                {
                    id: "minddeck",
                    label: t("nav.minddeck"),
                    href: "/tools/minddeck",
                    icon: FiCpu,
                },
                {
                    id: "jsonparser",
                    label: t("nav.jsonparser"),
                    href: "/tools/json-parser",
                    icon: FiCode,
                },
            ],
        },
        {
            id: "guide",
            label: t("nav.guide"),
            href: "/guide",
            icon: FiBook,
        },
        {
            id: "support",
            label: t("footer.support"),
            icon: FiHelpCircle,
            children: [
                {
                    id: "contact",
                    label: t("nav.contact"),
                    href: "/contact",
                    icon: FiMail,
                },
            ],
        },
        {
            id: "company",
            label: t("sidebar.companyInfo"),
            icon: FiInfo,
            children: [
                {
                    id: "about",
                    label: t("nav.about"),
                    href: "/about",
                    icon: FiInfo,
                },
                {
                    id: "privacy",
                    label: t("nav.privacy"),
                    href: "/privacy",
                    icon: FiShield,
                },
                {
                    id: "terms",
                    label: t("nav.terms"),
                    href: "/terms",
                    icon: FiFileText,
                },
            ],
        },
    ];

    const isActive = (path: string) => location.pathname === path;

    const toggleMobileExpanded = (itemId: string) => {
        setExpandedMobileItems((prev) =>
            prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId]
        );
    };

    const isMobileExpanded = (itemId: string) =>
        expandedMobileItems.includes(itemId);

    const renderMobileMenuItem = (item: MobileMenuItem, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const expanded = isMobileExpanded(item.id);
        const active = item.href ? isActive(item.href) : false;

        return (
            <div key={item.id}>
                {hasChildren ? (
                    <button
                        onClick={() => toggleMobileExpanded(item.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                            level === 0
                                ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        }`}
                        style={{ paddingLeft: `${1 + level * 1}rem` }}
                    >
                        <div className="flex items-center space-x-3">
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span>{item.label}</span>
                        </div>
                        <motion.div
                            animate={{ rotate: expanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <FiChevronRight className="w-4 h-4 flex-shrink-0" />
                        </motion.div>
                    </button>
                ) : (
                    <Link
                        to={item.href!}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                            active
                                ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                                : level === 0
                                ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        }`}
                        style={{ paddingLeft: `${1 + level * 1}rem` }}
                    >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        <span>{item.label}</span>
                    </Link>
                )}

                <AnimatePresence>
                    {hasChildren && expanded && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="mt-1 space-y-1"
                        >
                            {item.children!.map((child) =>
                                renderMobileMenuItem(child, level + 1)
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    };

    return (
        <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="container mx-auto px-4 lg:px-6">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex items-center space-x-2 text-xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent hover:scale-105 transition-transform"
                    >
                        <img
                            src={MewSeedsLogo}
                            alt="MewSeeds Logo"
                            className="w-48 h-auto object-contain mt-2"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-1">
                        {navItems.map((item) => (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                                    isActive(item.href)
                                        ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }`}
                            >
                                <item.icon className="w-4 h-4" />
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>

                    {/* Right Side Controls */}
                    <div className="flex items-center space-x-2">
                        {/* Language Selector */}
                        <div className="relative">
                            <button
                                onClick={() =>
                                    setIsLangMenuOpen(!isLangMenuOpen)
                                }
                                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                aria-label="Change Language"
                            >
                                <FiGlobe className="w-5 h-5" />
                            </button>

                            <AnimatePresence>
                                {isLangMenuOpen && (
                                    <motion.div
                                        initial={{
                                            opacity: 0,
                                            scale: 0.95,
                                            y: -10,
                                        }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{
                                            opacity: 0,
                                            scale: 0.95,
                                            y: -10,
                                        }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute right-0 mt-2 py-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700"
                                    >
                                        {languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                onClick={() => {
                                                    setLanguage(
                                                        lang.code as any
                                                    );
                                                    setIsLangMenuOpen(false);
                                                }}
                                                className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors ${
                                                    language === lang.code
                                                        ? "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400"
                                                        : ""
                                                }`}
                                            >
                                                <span className="text-lg">
                                                    {lang.flag}
                                                </span>
                                                <span className="text-sm font-medium">
                                                    {lang.name}
                                                </span>
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            aria-label="Toggle Theme"
                        >
                            {isDark ? (
                                <FiSun className="w-5 h-5 text-yellow-500" />
                            ) : (
                                <FiMoon className="w-5 h-5 text-gray-700" />
                            )}
                        </button>

                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() =>
                                setIsMobileMenuOpen(!isMobileMenuOpen)
                            }
                            className="md:hidden p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            aria-label="Toggle Menu"
                        >
                            {isMobileMenuOpen ? (
                                <FiX className="w-5 h-5" />
                            ) : (
                                <FiMenu className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.nav
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700"
                        >
                            <div className="space-y-2">
                                {mobileMenuItems.map((item) =>
                                    renderMobileMenuItem(item)
                                )}
                            </div>

                            {/* Quick Actions for Mobile */}
                            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3 px-4">
                                    빠른 링크
                                </h3>
                                <div className="space-y-2">
                                    <a
                                        href="https://minddeck.mewseeds.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center space-x-3 px-4 py-3 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                    >
                                        <FiCpu className="w-5 h-5" />
                                        <span>MindDeck 바로가기</span>
                                    </a>
                                    <a
                                        href="https://json.mewseeds.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center space-x-3 px-4 py-3 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                    >
                                        <FiCode className="w-5 h-5" />
                                        <span>JSON Parser 바로가기</span>
                                    </a>
                                </div>
                            </div>
                        </motion.nav>
                    )}
                </AnimatePresence>
            </div>
        </header>
    );
};

export default Header;
